/**
 * Momentum's vendored asset library (design.md V6.1 "Momentum asset library
 * integration"). Everything here resolves to a path under
 * `/assets/momentum/…` served from `web/public/assets/momentum/` (Vite's
 * public dir in dev; `web/dist/assets/momentum/…` via express static in
 * prod) - a vendored subset of
 * `~/Downloads/turn09_v3_proposal-agent-kit (1)/assets/`, downscaled and
 * copied in, never fetched at runtime.
 *
 * Asset resolver order (design.md V6.1, rule 3):
 *   content.imageUrl (client-supplied) -> product-specific kit asset ->
 *   template fallback asset -> (light informational pages only) no image.
 * Never a blank decorative shape - the kit's own asset-resolver rule
 * ("Never use a generic blank circle or large decorative shape as the
 * fallback for a missing hero asset") is why the old `ContourMotif` circle
 * component is gone; `Linework` (Motifs.tsx) is ambient background texture
 * layered *behind* whichever image this module resolves, never a
 * replacement for it.
 */

const BASE = '/assets/momentum';

/** design.md V6.1 default-art mapping, keyed by our config/catalog.json productIds. */
const PRODUCT_DEVICE: Record<string, string> = {
  paid_search: `${BASE}/devices/sem-laptop.png`, // sem/search -> sem-laptop
  social: `${BASE}/devices/meta-phone.png`, // meta/social -> meta-phone
  ctv: `${BASE}/devices/ctv-tv.png`,
  targeted_display: `${BASE}/devices/display-laptop-phone.png`, // display -> display-laptop-phone
  mobile_location: `${BASE}/devices/display-laptop-phone.png`, // nearest match: cross-device display targeting
  streaming_audio: `${BASE}/devices/pulse-laptop.png`, // no dedicated audio asset in the kit -> unknown fallback
  email: `${BASE}/devices/email-phone.png`,
  online_video: `${BASE}/devices/video-laptop.png`, // video/pre-roll -> video-laptop
};

/** Unknown/unmapped productId fallback (design.md: "unknown products -> pulse-laptop"). */
const DEFAULT_PRODUCT_DEVICE = `${BASE}/devices/pulse-laptop.png`;

/** Small icon per product, same taxonomy as the device art (media_mix legend). */
const PRODUCT_ICON: Record<string, string> = {
  paid_search: `${BASE}/icons/sem.png`,
  social: `${BASE}/icons/meta.png`,
  ctv: `${BASE}/icons/ctv.png`,
  targeted_display: `${BASE}/icons/display.png`,
  mobile_location: `${BASE}/icons/display.png`,
  streaming_audio: `${BASE}/icons/pulse.png`,
  email: `${BASE}/icons/email.png`,
  online_video: `${BASE}/icons/video.png`,
};

/** Template-owned fallback art for archetypes with no per-product concept. */
export const COVER_DEFAULT_IMAGE = `${BASE}/devices/cover-laptop-phone.png`;
export const OVERVIEW_DEFAULT_IMAGE = `${BASE}/photography/overview-comfort.webp`;
export const STRATEGY_DEFAULT_IMAGE = `${BASE}/photography/strategy-westside.webp`;
export const ARCHITECTURE_DEFAULT_IMAGE = `${BASE}/photography/campaign-architecture-westside.webp`;
export const WHY_US_DEFAULT_IMAGE = `${BASE}/people/why-us-team.png`;
export const THANK_YOU_DEFAULT_IMAGE = `${BASE}/people/closing-team-photo.png`;
export const REPORTING_DEFAULT_IMAGE = `${BASE}/reporting-dashboard.png`;

export const LINEWORK_DARK = `${BASE}/linework/linework-dark.svg`;
export const LINEWORK_LIGHT = `${BASE}/linework/linework-light.svg`;

/** Product-specific device art used by product_divider + product_education's right-card art. */
export function productDeviceAsset(productId: string | null | undefined): string {
  if (productId && PRODUCT_DEVICE[productId]) return PRODUCT_DEVICE[productId];
  return DEFAULT_PRODUCT_DEVICE;
}

/** Small per-product icon (media_mix legend). Null when no icon applies. */
export function productIconAsset(productId: string | null | undefined): string | null {
  if (!productId) return null;
  return PRODUCT_ICON[productId] ?? null;
}

/**
 * The resolver itself: content.imageUrl always wins; otherwise a
 * product-specific kit asset when productId is known; otherwise the
 * archetype's template fallback. Every momentum archetype that renders a
 * hero image passes a concrete `fallback`, so this always returns a src -
 * "dark pages never render empty heroes" (design.md V6.1, rule 2) applies
 * to every caller, not just the dark ones.
 */
export function resolveHeroImage({
  imageUrl,
  productId,
  fallback,
}: {
  imageUrl?: string | null;
  productId?: string | null;
  fallback: string;
}): string {
  if (imageUrl) return imageUrl;
  if (productId) return productDeviceAsset(productId);
  return fallback;
}

/** True when the src is vendored kit art (transparent compositions designed
 * to float with air on dark grounds) rather than a client-supplied photo. */
export function isKitAsset(src: string): boolean {
  return src.startsWith('/assets/momentum/');
}
